import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import Avatar from '~/components/Avatar'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const AvatarPage: Component = () => {
  const placeholder = 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=256&h=256&auto=format&fit=crop'
  const femalePlaceholder = 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&h=256&auto=format&fit=crop'

  return (
    <>
      <Title>Avatar Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">Avatar</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Representación visual de usuarios, perfiles o entidades. Soporta imágenes, estados, iniciales automáticas y diversos tamaños y formas.
          </p>
        </div>

        <div class="space-y-12">
          {/* Tamaños y Fallbacks */}
          <Card>
            <Card.Header
              title="Escalabilidad y Fallbacks"
              subtitle="Desde miniaturas hasta perfiles destacados con generación de iniciales automática."
              icon={<Icon icon="material-symbols-light:maximize" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col gap-10">
                <div class="flex flex-wrap items-end gap-6 p-6 bg-surface-muted rounded-xl border border-border">
                  <div class="flex flex-col items-center gap-2">
                    <Avatar size="xs" src={femalePlaceholder} alt="XS" />
                    <span class="text-[10px] font-mono text-foreground-subtle font-bold uppercase">xs</span>
                  </div>
                  <div class="flex flex-col items-center gap-2">
                    <Avatar size="sm" src={femalePlaceholder} alt="SM" />
                    <span class="text-[10px] font-mono text-foreground-subtle font-bold uppercase">sm</span>
                  </div>
                  <div class="flex flex-col items-center gap-2">
                    <Avatar size="md" src={femalePlaceholder} alt="MD" />
                    <span class="text-[10px] font-mono text-foreground-subtle font-bold uppercase">md</span>
                  </div>
                  <div class="flex flex-col items-center gap-2">
                    <Avatar size="lg" src={femalePlaceholder} alt="LG" />
                    <span class="text-[10px] font-mono text-foreground-subtle font-bold uppercase">lg</span>
                  </div>
                  <div class="flex flex-col items-center gap-2">
                    <Avatar size="xl" src={femalePlaceholder} alt="XL" />
                    <span class="text-[10px] font-mono text-foreground-subtle font-bold uppercase">xl</span>
                  </div>
                  <div class="flex flex-col items-center gap-2">
                    <Avatar size={96} src={femalePlaceholder} alt="96px" />
                    <span class="text-[10px] font-mono text-foreground-subtle font-bold uppercase">96px</span>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div class="p-6 bg-surface rounded-xl border border-border flex items-center gap-6 shadow-sm">
                    <Avatar size="lg" alt="Juan Pérez" />
                    <div>
                      <h4 class="font-bold text-foreground">Fallback por Texto</h4>
                      <p class="text-sm text-foreground-subtle">Genera iniciales basadas en el nombre si la URL es inválida.</p>
                    </div>
                  </div>
                  <div class="p-6 bg-surface rounded-xl border border-border flex items-center gap-6 shadow-sm">
                    <Avatar size="lg" src="broken-link" alt="User Placeholder" />
                    <div>
                      <h4 class="font-bold text-foreground">Error de Carga</h4>
                      <p class="text-sm text-foreground-subtle">Muestra un icono o iniciales si la imagen falla al cargar.</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Formas y Estados */}
          <Card>
            <Card.Header
              title="Formas y Estados"
              subtitle="Personaliza la geometría del avatar y combínalo con componentes de estado."
              icon={<Icon icon="material-symbols-light:account-circle-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-12 p-8 bg-surface-muted rounded-xl border border-border">
                <div class="space-y-4">
                  <h4 class="font-bold text-foreground">Variantes de Forma</h4>
                  <div class="flex gap-6 items-center">
                    <div class="text-center space-y-2">
                      <Avatar size="xl" rounded="full" src={placeholder} alt="Full" />
                      <p class="text-xs text-foreground-subtle font-medium">full</p>
                    </div>
                    <div class="text-center space-y-2">
                      <Avatar size="xl" rounded="square" src={femalePlaceholder} alt="Square" />
                      <p class="text-xs text-foreground-subtle font-medium">square</p>
                    </div>
                  </div>
                </div>

                <div class="space-y-4">
                  <h4 class="font-bold text-foreground">Combinación con Badges</h4>
                  <div class="flex gap-8 items-center">
                    <div class="relative">
                      <Badge dot color="success" position="bottom-right">
                        <Avatar size="xl" src={placeholder} alt="Online" />
                      </Badge>
                      <p class="text-center text-[10px] mt-2 text-foreground-subtle uppercase tracking-wider">Online</p>
                    </div>
                    <div class="relative">
                      <Badge count={5} color="danger">
                        <Avatar size="xl" src={femalePlaceholder} alt="Notifications" />
                      </Badge>
                      <p class="text-center text-[10px] mt-2 text-foreground-subtle uppercase tracking-wider">Messages</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Avatar Groups */}
          <Card>
            <Card.Header
              title="Grupos de Avatares"
              subtitle="Ideal para mostrar miembros de un equipo o participantes de un hilo."
              icon={<Icon icon="material-symbols-light:group-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="p-10 flex flex-col items-center justify-center bg-surface-muted rounded-xl border border-border">
                <div class="flex -space-x-4 hover:space-x-1 transition-all duration-300">
                  <Avatar size={48} src="https://i.pravatar.cc/150?u=1" alt="A1" class="ring-4 ring-surface" />
                  <Avatar size={48} src="https://i.pravatar.cc/150?u=2" alt="A2" class="ring-4 ring-surface" />
                  <Avatar size={48} src="https://i.pravatar.cc/150?u=3" alt="A3" class="ring-4 ring-surface" />
                  <Avatar size={48} src="https://i.pravatar.cc/150?u=4" alt="A4" class="ring-4 ring-surface" />
                  <div class="w-12 h-12 rounded-full bg-surface-muted flex items-center justify-center text-xs font-bold ring-4 ring-surface text-foreground-muted">+12</div>
                </div>
                <p class="mt-6 text-sm text-foreground-subtle text-center italic leading-relaxed">
                  Sugerencia: Usa <span class="font-mono bg-surface-muted px-1 rounded">-space-x-*</span> y una clase <span class="font-mono bg-surface-muted px-1 rounded">ring-*</span> para lograr el
                  efecto de superposición profesional.
                </p>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Manual de propiedades para configurar el componente Avatar."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Default</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">src</td>
                      <td class="px-6 py-4 text-foreground-subtle">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-foreground-muted">URL de la imagen. Si falla la carga, muestra iniciales.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">alt</td>
                      <td class="px-6 py-4 text-foreground-subtle font-bold italic">string (required)</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Texto descriptivo; se usa para generar iniciales como fallback.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">size</td>
                      <td class="px-6 py-4 text-foreground-subtle">number | MainSizesType</td>
                      <td class="px-6 py-4 font-mono">'md' (40px)</td>
                      <td class="px-6 py-4 text-foreground-muted">Tamaño del componente. Acepta px o tokens (xs, sm, md, lg, xl).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">rounded</td>
                      <td class="px-6 py-4 text-foreground-subtle">'full' | 'square'</td>
                      <td class="px-6 py-4 font-mono">'full'</td>
                      <td class="px-6 py-4 text-foreground-muted">Forma geométrica del avatar.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">class</td>
                      <td class="px-6 py-4 text-foreground-subtle">string</td>
                      <td class="px-6 py-4 font-mono">''</td>
                      <td class="px-6 py-4 text-foreground-muted">Clases personalizadas para el contenedor.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default AvatarPage
